import { useEffect, useState } from "react";
import { Bell, CheckCheck, Target, CheckSquare, AlertTriangle, ArrowRightLeft } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { relativeTime } from "@/lib/format";

const ICONS = {
  lead_assigned: Target,
  task_assigned: CheckSquare,
  task_overdue: AlertTriangle,
  stage_changed: ArrowRightLeft,
};

export function NotificationCenter() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try { const res = await api.get("/notifications"); setData(res.data); } catch (e) { /* */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const openItem = async (n) => {
    await api.patch(`/notifications/${n.id}/read`);
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-trigger" className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50">
          <Bell className="h-[18px] w-[18px]" />
          {data.unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold text-white" style={{ backgroundColor: "#E31E24" }}>
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className="text-sm font-semibold text-slate-800">Notifications</span>
          {data.unread > 0 && (
            <button data-testid="notifications-mark-all" onClick={markAll} className="flex items-center gap-1 text-xs font-medium text-[#0057A8]">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">You're all caught up 🎉</div>
          ) : (
            data.items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button key={n.id} onClick={() => openItem(n)}
                  className={`flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition hover:bg-slate-50 ${!n.read ? "bg-blue-50/40" : ""}`}>
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800">{n.title}</div>
                    <div className="truncate text-xs text-slate-500">{n.message}</div>
                    <div className="mt-0.5 text-[10px] text-slate-400">{relativeTime(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: "#0057A8" }} />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
